/**
 * Promise를 반환하는 범용 JSONP(JSON with Padding) 요청 함수
 * @param baseUrl 요청할 API의 기본 URL
 * @param params URL에 추가할 쿼리 파라미터 객체
 * @returns API 응답 데이터를 담은 Promise
 */
export const jsonpRequest = (
  baseUrl: string,
  params: Record<string, string>,
  timeout: number = 10000,
): Promise<any> => {
  return new Promise((resolve, reject) => {
    // 1. 매번 고유한 콜백 함수 이름 생성 (요청 충돌 방지)
    const callbackName = `jsonp_callback_${Date.now()}_${Math.round(100000 * Math.random())}`;
    const script = document.createElement('script');
    let timeoutId: NodeJS.Timeout | null = null;

    // 정리 함수
    const cleanup = () => {
      if (script.parentNode) {
        document.body.removeChild(script);
      }
      delete (window as any)[callbackName];
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

    // 2. 전역 콜백 함수 정의: 스크립트가 로드되면 이 함수가 실행됨
    (window as any)[callbackName] = (data: any) => {
      // 3. 성공 시: 생성했던 스크립트 태그와 전역 콜백 함수를 깔끔하게 정리
      cleanup();
      // 4. Promise를 성공 상태로 만들고 데이터 반환
      resolve(data);
    };

    // 스크립트 로드 실패 시 에러 처리
    script.onerror = () => {
      cleanup();
      reject(new Error('JSONP request failed.'));
    };

    // 타임아웃 설정
    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error('JSONP request timeout.'));
    }, timeout);

    // 5. 파라미터와 콜백 함수 이름을 포함한 전체 URL 생성
    const url = new URL(baseUrl);
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
    // VWorld API는 'callback' 파라미터로 콜백 함수 이름을 받음
    url.searchParams.set('callback', callbackName);

    // 6. 스크립트 태그를 body에 추가하여 실제 요청 시작
    script.src = url.toString();
    document.body.appendChild(script);
  });
};
